import React, { useState, useEffect } from "react";
import { Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import Button from "components/atoms/Button/Button";
import usePermissions from "hooks/usePermissions";
import { ReactComponent as QuickRequestIcon } from "assets/images/icons/Bolt.svg";
import { ReactComponent as QuoteRequestIcon } from "assets/images/icons/Calculator.svg";
import { ReactComponent as ShipmentIcon } from "assets/images/icons/Ship.svg";
import { ReactComponent as TrackerIcon } from "assets/images/icons/TrackerIcon.svg";

import "./QuickActionsWidget.scss";
import { getWidgetQuickActionsTranslations } from "helpers/translationHelpers";

function QuickActionsWidget({ isLayoutLocked, isPreview, widgetContent, widgetId, handleWidgetDialogOpen }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [quickActions, setQuickActions] = useState([]);
  const { permissions } = usePermissions();
  const { quoting } = JSON.parse(permissions).permissions;
  const { create_quick_requests, create_quote_requests } = quoting;

  useEffect(() => {
    if (widgetContent) {
      const actions = JSON.parse(widgetContent);
      const updatedActions = actions.map((action) => {
        if (action.type === "create_quote_request" && !create_quote_requests) {
          return { ...action, disabled: true };
        }
        if (action.type === "create_quick_request" && !create_quick_requests) {
          return { ...action, disabled: true };
        }
        return action;
      });
      setQuickActions(updatedActions);
    }
  }, [widgetContent]);

  const handleNavigate = (action) => {
    let path = "";
    switch (action.type) {
      case "create_quote_request":
        path = "/quote-request";
        break;
      case "create_quick_request":
        path = "/quick-request";
        break;
      case "new_shipment":
        path = "/create-new-booking";
        break;
      case "tracker":
        path = "/tracker";
        break;
      default:
        path = "";
    }
    if (isPreview) return;
    if (!action.template.id) {
      navigate(path);
    } else {
      navigate(`${path}?template_id=${action?.template?.id}`);
    }
  };

  return (
    <Card.Body
      className="overflow-auto"
      style={{
        maxHeight: isPreview ? "300px" : "100%",
        overflowY: "auto",
      }}
    >
      <div
        className={`d-flex justify-content-${
          isLayoutLocked ? "center" : "between"
        } align-items-center mb-2 pb-2 br-5 bg-white rounded-bottom-left-0 rounded-bottom-right-0`}
      >
        <h5 className="pl-1 mb-1">
          <FormattedMessage id="dashboard.widgetDialog.widgetType.quickActions" defaultMessage="Quick actions" />
        </h5>
        {!isLayoutLocked && !isPreview && (
          <Button
            className="edit-btn"
            onClick={() => {
              handleWidgetDialogOpen(widgetId);
            }}
          >
            <FormattedMessage id="general.edit" defaultMessage="Edit" />
          </Button>
        )}
      </div>
      {quickActions?.length === 0 && (
        <div className="text-center text-secondary p-4">
          <p>
            <FormattedMessage
              id="dashboard.widgetDialog.widgetType.quickActions.noActionsSelected"
              defaultMessage="No quick actions selected"
            />
          </p>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gridGap: "5px" }}>
        {quickActions &&
          quickActions.length > 0 &&
          quickActions.map((action) => {
            const templateName = action.type
              .replace(/_/g, " ")
              .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
            const label = getWidgetQuickActionsTranslations(
              action.template.name === "" ? templateName : `Create ${action.template.name}`,
              intl,
            );

            // there are 3 action.type values: create_quote_request, create_quick_request, create_shipment
            // we need to render different icons for each type
            let renderIcon = null;
            switch (action.type) {
              case "create_quote_request":
                renderIcon = <QuoteRequestIcon />;
                break;
              case "create_quick_request":
                renderIcon = <QuickRequestIcon />;
                break;
              case "new_shipment":
                renderIcon = <ShipmentIcon />;
                break;
              case "tracker":
                renderIcon = <TrackerIcon />;
                break;
              default:
                renderIcon = null;
            }

            return action.disabled ? (
              <OverlayTrigger
                placement="top"
                key={action.id}
                overlay={
                  <Tooltip id={`tooltip-${action.id}`}>
                    <FormattedMessage
                      id="general.noPermission"
                      defaultMessage="You do not have permission to perform this action"
                    />
                  </Tooltip>
                }
              >
                <div key={action.id} className="quick-action-item quick-action-item-disabled font-b2">
                  <span>{renderIcon}</span>
                  <p className="font-b2 ml-3">yo</p>
                </div>
              </OverlayTrigger>
            ) : (
              <div
                key={action.id}
                className="quick-action-item font-b2"
                onClick={() => {
                  handleNavigate(action);
                }}
              >
                <span>{renderIcon}</span>
                <p className="font-b2 ml-3">{label}</p>
              </div>
            );
          })}
      </div>
    </Card.Body>
  );
}

QuickActionsWidget.propTypes = {
  isLayoutLocked: PropTypes.bool,
  isPreview: PropTypes.bool,
  widgetContent: PropTypes.string.isRequired,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
};

QuickActionsWidget.defaultProps = {
  isLayoutLocked: false,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default QuickActionsWidget;
